/**
 * Sanitize relaxed JSON input to make it parseable by JSON.parse()
 * Handles common issues like missing quotes, trailing commas, single quotes, etc.
 */
export const sanitizeJsonInput = (input: string): string => {
  let sanitized = input.trim();
  
  // Remove single line comments
  sanitized = sanitized.replace(/\/\/.*$/gm, '');
  
  // Remove multi-line comments
  sanitized = sanitized.replace(/\/\*[\s\S]*?\*\//g, '');
  
  // Replace single quotes with double quotes
  sanitized = sanitized.replace(/'/g, '"');
  
  // Remove trailing commas before } or ]
  sanitized = sanitized.replace(/,(\s*[}\]])/g, '$1');
  
  // Add quotes around unquoted keys
  sanitized = sanitized.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
  
  // Handle edge case where the first key doesn't have a comma before it
  sanitized = sanitized.replace(/^(\s*{)\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
  
  // Try to fix unquoted string values (simple cases only)
  sanitized = sanitized.replace(/:\s*([a-zA-Z_$][a-zA-Z0-9_$\s]*?)(\s*[,}\]])/g, (match, value, ending) => {
    const trimmedValue = value.trim();
    // Don't quote if it's a boolean, number, null, or already starts with quote/bracket
    if (trimmedValue === 'true' || trimmedValue === 'false' || 
        trimmedValue === 'null' || trimmedValue === 'undefined' ||
        /^-?\d+\.?\d*$/.test(trimmedValue) || 
        trimmedValue.startsWith('"') || trimmedValue.startsWith('[') || 
        trimmedValue.startsWith('{')) {
      return ':' + value + ending;
    }
    return ': "' + trimmedValue + '"' + ending;
  });
  
  // Convert undefined to null
  sanitized = sanitized.replace(/:\s*undefined/g, ': null');
  
  return sanitized;
};

/**
 * Try to parse JSON with relaxed syntax first, fallback to strict parsing
 */
export const parseRelaxedJson = (input: string): any => {
  // First try to parse as-is (strict JSON)
  try {
    return JSON.parse(input);
  } catch (strictError) {
    // If strict parsing fails, try with sanitization
    try {
      const sanitized = sanitizeJsonInput(input);
      return JSON.parse(sanitized);
    } catch (relaxedError) {
      // If both fail, throw the original strict error for better error messages
      throw strictError;
    }
  }
};

/**
 * Validate and provide helpful error messages for common JSON mistakes
 */
export const validateAndFixJson = (input: string): { 
  success: boolean; 
  data?: any; 
  error?: string; 
  wasFixed?: boolean;
  fixedInput?: string;
} => {
  if (!input.trim()) {
    return { success: false, error: 'Vui lòng nhập JSON để xử lý' };
  }

  try {
    // Try strict parsing first
    const strictData = JSON.parse(input);
    return { success: true, data: strictData, wasFixed: false };
  } catch (strictError) {
    // Try with sanitization
    try {
      const sanitized = sanitizeJsonInput(input);
      const data = JSON.parse(sanitized);
      return { 
        success: true, 
        data, 
        wasFixed: true, 
        fixedInput: sanitized 
      };
    } catch (relaxedError) {
      // Provide helpful error messages for common mistakes
      const errorMessage = (strictError as Error).message;
      let helpfulMessage = 'JSON không hợp lệ: ' + errorMessage;
      
      if (errorMessage.includes('Unexpected token')) {
        helpfulMessage += '\n\nGợi ý: Kiểm tra các lỗi phổ biến:\n' +
          '• Thiếu dấu ngoặc kép quanh keys\n' +
          '• Dấu phay thừa ở cuối\n' +
          '• Sử dụng single quotes thay vì double quotes';
      }
      
      return { success: false, error: helpfulMessage };
    }
  }
};
